import './toolPreview.scss';

import React                  from 'react';
import View                   from '@widesk-ui/components/View';
import Popover                from '@widesk-ui/components/Popover';
import { PlayCircleFilled }   from '@ant-design/icons';
import { PlayCircleOutlined } from '@ant-design/icons';
import Kbd                    from '@/TokamapEditor/ux/Kbd';
import useDesignTokens        from '@/hooks/useDesignTokens';

const DESCRIPTIONS = {
	rectangle: 'Cliquez un premier coin, puis le coin opposé.',
	polygon: 'Un clic par sommet, puis un clic sur le premier pour refermer la forme.',
	circle: 'Cliquez le centre, puis un point du bord pour fixer le rayon.',
	line: 'Un clic par point, puis un nouveau clic sur le dernier pour terminer : une cloison, un cheminement.',
	marker: 'Un clic pose un point sur la carte, sans surface.',
	image: 'Choisissez une image, puis cliquez sur la carte pour la poser. Elle se déplace, tourne et se redimensionne comme une forme.',
	decor: 'Choisissez un élément dans le catalogue, puis cliquez sur la carte pour le poser.',
	opening: 'Glissez le long du mur d’une forme pour y percer une ouverture. Un clic dans une ouverture la referme.',
	cut: 'Tracez une ligne à travers une forme pour la partager, ou refermez-la sur son premier point pour détourer une pièce à l’intérieur.',
	buildingPick: 'Cliquez sur un bâtiment du fond de carte pour reprendre son contour en forme.',
	arrivalFlight: 'À l’ouverture de la carte, la caméra arrive d’une vue large et plonge vers la vue de départ. Un geste du visiteur l’interrompt.',
	spotlight: 'Le stand trouvé par la recherche, ou dont on ouvre la fiche, reste éclairé pendant que le reste de la carte s’assombrit.',
	geolocation: 'Le visiteur voit sa position sur la carte, et peut s’en servir comme point de départ d’un itinéraire.',
	reliefVolume: 'La forme s’élève en volume. Visible quand la carte est inclinée.',
	reliefDug: 'La forme s’enfonce dans le sol, comme une cour en contrebas ou un patio. Visible quand la carte est inclinée.',
	placePanel: 'Au clic sur la forme, le visiteur voit la fiche du point d’intérêt par-dessus le plan : son nom, son image et ses informations.',
	contactShadows: 'Une ombre douce au pied des stands et des murs, étirée à l’opposé du soleil. Visible en 3D.',
	decorShadows: 'Une tache d’ombre sous le mobilier, les arbres, les véhicules et les personnages. Visible en 3D.',
	planImport: 'Partez de votre plan DXF, SVG, PDF ou image : les emplacements et leurs numéros sont reconnus, puis créés en formes sur la carte.',
};

export type ToolPreviewName = keyof typeof DESCRIPTIONS;

const PREVIEW_DELAY_MS = 400;
const PREVIEW_WIDTH = 300;

const ToolPreview = (props: {
	name: ToolPreviewName;
	label: React.ReactNode;
	shortCut?: string;
	placement?: 'left' | 'right';
	children: React.ReactNode;
}) => {
	const placement = props.placement ?? 'left';
	const tokens = useDesignTokens();
	const [shown, setShown] = React.useState(false);
	const timer = React.useRef<ReturnType<typeof setTimeout>>();
	const video = React.useRef<HTMLVideoElement>(null);

	React.useEffect(() => () => clearTimeout(timer.current), []);

	React.useEffect(() => {
		if (!video.current) return;
		if (!shown) return video.current.pause();

		video.current.currentTime = 0;
		void video.current.play();
	}, [shown]);

	const hide = () => {
		clearTimeout(timer.current);
		setShown(false);
	};

	return (
		<Popover
			open={shown}
			placement={placement}
			arrow={false}
			styles={{
				root: placement === 'left' ? { paddingRight: 10 } : { paddingLeft: 10 },
				body: { padding: 0, width: PREVIEW_WIDTH, overflow: 'hidden', borderRadius: 12, boxShadow: tokens.shadow.elevated },
			}}
			content={(
				<View>
					<View rel>
						<video
							ref={video}
							src={`${import.meta.env.BASE_URL}tool-previews/${props.name}.webm`}
							width={PREVIEW_WIDTH}
							height={PREVIEW_WIDTH * 9 / 16}
							autoPlay
							muted
							loop
							playsInline
							style={{ display: 'block', background: tokens.surface.subtle, borderBottom: `1px solid ${tokens.border.subtle}` }}
						/>

						<View
							row
							centerV
							gap={4}
							size={11}
							bold
							color={tokens.text.default}
							style={{
								position: 'absolute',
								left: 8,
								bottom: 8,
								padding: '2px 8px 2px 6px',
								borderRadius: 999,
								background: `color-mix(in srgb, ${tokens.surface.card} 88%, transparent)`,
								boxShadow: tokens.shadow.card,
							}}
						>
							<PlayCircleFilled style={{ fontSize: 12, color: tokens.color.primary }} />
							Démo
						</View>
					</View>

					<View gap={4} px={14} py={12}>
						<View row centerV gap="sm">
							<View flex bold>{props.label}</View>

							{props.shortCut && <Kbd>{props.shortCut}</Kbd>}
						</View>

						<View size={12.5} color={tokens.text.secondary} style={{ lineHeight: 1.45 }}>
							{DESCRIPTIONS[props.name]}
						</View>
					</View>
				</View>
			)}
		>
			<div
				className="tool-preview-anchor"
				onMouseEnter={() => {
					timer.current = setTimeout(() => setShown(true), PREVIEW_DELAY_MS);
				}}
				onMouseLeave={hide}
				onMouseDown={hide}
			>
				{props.children}
			</div>
		</Popover>
	);
};

export const DemoMark = () => {
	const tokens = useDesignTokens();

	return (
		<PlayCircleOutlined
			className="demo-mark"
			aria-label="Démo disponible au survol"
			style={{ fontSize: 12, marginLeft: 4, color: tokens.text.muted, '--demo-mark-hover': tokens.color.primary } as React.CSSProperties}
		/>
	);
};

export default ToolPreview;
